import React, { useState, useRef, useEffect } from "react";
import { useBusiness } from "@/contexts/BusinessContext";
import { ChevronDown, Building2, Car, Loader2, ShoppingBag } from "lucide-react";

export default function BusinessSelector() {
  const { businesses, currentBusiness, selectBusiness, loading } = useBusiness();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        <span className="text-slate-400 text-sm">Cargando...</span>
      </div>
    );
  }

  const getIcon = (businessId) => {
    if (businessId === "maquinaria") return Building2;
    if (businessId === "comercializadora") return ShoppingBag;
    return Car;
  };

  const getAccentColor = (business) => {
    return business?.accent_color || "#3B82F6";
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors border border-slate-700 min-w-[220px]"
        data-testid="business-selector-btn"
      >
        {currentBusiness && (
          <>
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${getAccentColor(currentBusiness)}20` }}
            >
              {React.createElement(getIcon(currentBusiness.id), {
                className: "w-4 h-4",
                style: { color: getAccentColor(currentBusiness) }
              })}
            </div>
            <div className="text-left flex-1">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Negocio</p>
              <p className="text-white font-medium text-sm truncate">
                {currentBusiness.logo_text || currentBusiness.name}
              </p>
            </div>
          </>
        )}
        <ChevronDown 
          className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
          {businesses.map((business) => {
            const Icon = getIcon(business.id);
            const isSelected = currentBusiness?.id === business.id;
            
            return (
              <button
                key={business.id}
                onClick={() => {
                  selectBusiness(business.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isSelected 
                    ? 'bg-slate-700' 
                    : 'hover:bg-slate-700/50'
                }`}
                data-testid={`business-option-${business.id}`}
              >
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${business.accent_color}20` }}
                >
                  <Icon 
                    className="w-4 h-4"
                    style={{ color: business.accent_color }}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">{business.name}</p>
                  <p className="text-slate-500 text-xs">{business.description}</p>
                </div>
                {isSelected && (
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: business.accent_color }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
